import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { login, register } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plane, Mail, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = isLogin 
        ? await login(email, password)
        : await register(email, password);

      const { token, user } = response.data;
      loginUser(token, user);

      if (user.must_change_password) {
        toast.info('Por favor, defina uma nova senha');
        navigate('/change-password');
      } else {
        toast.success(isLogin ? 'Bem-vindo!' : 'Conta criada com sucesso!');
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Erro ao processar solicitação';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

 return (
  <div className="min-h-screen flex flex-col md:flex-row">
    <div
      className="hidden md:block md:w-1/2 relative bg-cover bg-center"
      style={{ backgroundImage: 'url("/img/Simulados-VOAR.png")' }}
    >
      <div className="absolute inset-0 flex flex-col justify-end p-12">
          <h1 className="text-5xl font-black text-white mb-4">
            Simulados V.O.A.R.
          </h1>
          <p className="text-xl text-zinc-300 max-w-md">
            Simulados exclusivos para alunos do Método V.O.A.R. Prepare-se para o exame de Piloto Privado.
          </p>
        </div>
      </div>

      {/* Form Side */}
      <div className="auth-form bg-zinc-100">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo-voar.png" alt="Simulados V.O.A.R." className="h-12" />
            <div>
              <h2 className="text-lg font-bold text-zinc-900">Simulados V.O.A.R.</h2>
              <p className="text-xs text-zinc-500">Simulados exclusivos para alunos do Método V.O.A.R.</p>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-3xl font-bold text-zinc-900 mb-2">
            {isLogin ? 'Entrar' : 'Criar Conta'}
          </h3>
          <p className="text-zinc-500 mb-8">
            {isLogin 
              ? 'Acesse sua conta para continuar seus estudos'
              : 'Use a senha padrão "pilotovoar" no primeiro acesso'}
          </p>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg mb-6 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="form-label">E-mail</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 z-10" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="h-12 pl-12 bg-white border border-zinc-200 rounded-lg focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="form-label">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 z-10" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-12 bg-white border border-zinc-200 rounded-lg focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            {isLogin && (
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Esqueceu a senha?
                </Link>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-medium rounded-lg btn-press"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Aguarde...' : isLogin ? 'Entrar' : 'Criar Conta'}
            </Button>
          </form>

          {/* Toggle */}
          <p className="mt-8 text-center text-zinc-500">
            {isLogin ? 'Novo por aqui?' : 'Já tem uma conta?'}{' '}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-zinc-900 font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Criar conta' : 'Fazer login'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
